import type { MessengerMessage } from '@/api/client';
import { isMessengerOptimisticMessageId } from '@/constants/messengerOptimistic';

function dmMsgElement(viewport: Element, messageId: string): Element | null {
  const sel =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? `[data-dm-msg="${CSS.escape(messageId)}"]`
      : `[data-dm-msg="${messageId.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`;
  return viewport.querySelector(sel);
}

/**
 * Самое «нижнее» по таймлайну сообщение, пересекающее видимую область viewport по вертикали
 * (как в Telegram: прочитано до последнего реально попавшего в экран).
 */
export function findLastVisibleDmMessageId(
  viewport: HTMLElement | null,
  orderedMessageIds: readonly string[]
): string | null {
  if (!viewport) return null;
  const vr = viewport.getBoundingClientRect();
  let last: string | null = null;
  for (const id of orderedMessageIds) {
    if (isMessengerOptimisticMessageId(id)) continue;
    const el = dmMsgElement(viewport, id);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    const overlapsY = r.bottom > vr.top && r.top < vr.bottom;
    if (overlapsY) last = id;
  }
  return last;
}

/**
 * Последнее реально видимое входящее сообщение.
 * Собственные сообщения не двигают read-cursor и не должны триггерить mark-read после отправки.
 */
export function findLastVisibleDmPeerMessageId(
  viewport: HTMLElement | null,
  orderedMessages: readonly Pick<MessengerMessage, 'id' | 'sender_id'>[],
  currentUserId: string | null
): string | null {
  if (!viewport || !currentUserId) return null;
  const vr = viewport.getBoundingClientRect();
  let last: string | null = null;
  for (const msg of orderedMessages) {
    if (isMessengerOptimisticMessageId(msg.id)) continue;
    if (String(msg.sender_id) === String(currentUserId)) continue;
    const el = dmMsgElement(viewport, msg.id);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    const overlapsY = r.bottom > vr.top && r.top < vr.bottom;
    if (overlapsY) last = msg.id;
  }
  return last;
}

/** Есть ли вертикальное пересечение пузыря с видимой областью скролла. */
export function isDmMessageIdVerticallyVisible(
  viewport: HTMLElement | null,
  messageId: string
): boolean {
  if (!viewport) return false;
  const el = dmMsgElement(viewport, messageId);
  if (!el) return false;
  const vr = viewport.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return r.bottom > vr.top && r.top < vr.bottom;
}
